/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * The typed lines of the invite screen, without a screen.
 *
 * Everything here is a pure function over the rows, so the rules that decide
 * what may be sent — the per-plan ceiling, the duplicate, the pasted list that
 * runs out of seats — can be exercised without a browser. The component maps
 * the codes these return onto strings; it does not decide anything.
 */

import type { TeamSeatBlock } from '@/services/requests/housien-services/AgenciesService.team';

/** Deliberately permissive: the address is confirmed by the mail arriving. */
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** A pasted list may be separated by spaces, commas, semicolons or newlines. */
const SEPARATORS = /[\s,;]+/;

/** One typed line. `touched` keeps the error quiet until the operator leaves. */
export interface RowDraft {
  id: string;
  email: string;
  role: string;
  touched?: boolean;
}

/**
 * What is wrong with one line, or '' when nothing is.
 *
 * `rowError` only ever returns the first three: they are what the screen can
 * see for itself. `no_seats` and `send_failed` come back from the batch — a
 * seat taken between loading and sending, a mail server having a bad moment —
 * and land on the same line, because that is where the operator is looking.
 */
export type RowError =
  | ''
  | 'invalid'
  | 'duplicate'
  | 'already_member'
  | 'no_seats'
  | 'send_failed';

/** The rows of every plan, keyed by plan id. */
export type RowsByPlan = Record<string, RowDraft[]>;

/** An address as it will be stored and compared. */
export const normalise = (value: string): string => value.trim().toLowerCase();

/** A pasted blob, split into the addresses it holds. */
export const splitPasted = (text: string): string[] => (
  (text || '').split(SEPARATORS).map((part) => part.trim()).filter(Boolean)
);

/**
 * What is wrong with one line, read against every line and the whole team.
 *
 * An empty line is not an error: it is the row waiting to be typed in, and
 * marking it red the moment it appears would make the screen shout at
 * somebody who has not done anything yet.
 */
export const rowError = (
  email: string,
  allRows: RowDraft[],
  seatedEmails: string[],
): '' | 'invalid' | 'duplicate' | 'already_member' => {
  const value = normalise(email);
  if (!value) return '';
  if (!EMAIL.test(value)) return 'invalid';
  if (seatedEmails.some((seated) => normalise(seated) === value)) return 'already_member';
  if (allRows.filter((row) => normalise(row.email) === value).length > 1) return 'duplicate';
  return '';
};

/** Every line typed anywhere on the screen, in plan order. */
export const allRowsOf = (blocks: TeamSeatBlock[], rows: RowsByPlan): RowDraft[] => (
  blocks.flatMap((block) => rows[block.plan_id] || [])
);

/** How many lines carry an address — the number the send button quotes. */
export const readyCount = (blocks: TeamSeatBlock[], rows: RowsByPlan): number => (
  allRowsOf(blocks, rows).filter((row) => row.email.trim()).length
);

/** Seats left across every plan. */
export const freeTotal = (blocks: TeamSeatBlock[]): number => (
  blocks.reduce((total, block) => total + Math.max(0, block.free), 0)
);

/** Lines still typeable in this plan: its free seats, minus what is typed. */
export const roomLeft = (block: TeamSeatBlock, rows: RowDraft[]): number => Math.max(
  0, Math.max(0, block.free) - rows.filter((row) => row.email.trim()).length,
);

/**
 * A plan may take another line while it still has a seat for it.
 *
 * Counted against the rows themselves, not the filled ones: two blank rows
 * and one seat would otherwise keep offering a third.
 */
export const canAddRow = (block: TeamSeatBlock, rows: RowDraft[]): boolean => (
  rows.length < Math.max(0, block.free)
);

/**
 * The rows a plan starts with: one empty line, unless it has no seat at all.
 *
 * A plan with every seat taken opens with nothing to type in, and the screen
 * says so instead of offering a row that can never be sent.
 */
export const openingRows = (block: TeamSeatBlock, made: RowDraft): RowDraft[] => (
  block.free > 0 ? [made] : []
);

/** What a paste did to one plan's rows, and what it could not fit. */
export interface PasteResult {
  rows: RowDraft[];
  overflow: number;
}

/**
 * Spread a pasted list over the rows, starting at the one pasted into.
 *
 * The lines already typed below the cursor are kept — pasting into the second
 * row of four must not wipe the two under it. What does not fit is reported
 * rather than dropped, because a batch that quietly lost four of six
 * addresses is a team that never arrives with nobody able to say why.
 */
export const applyPaste = (
  rows: RowDraft[],
  rowId: string,
  parts: string[],
  free: number,
  makeRow: (email: string, role: string) => RowDraft,
): PasteResult => {
  const index = rows.findIndex((row) => row.id === rowId);
  const current = rows[index];
  const before = rows.slice(0, index);
  const after = rows.slice(index + 1).filter((row) => row.email.trim());
  const typedElsewhere = before.filter((row) => row.email.trim()).length + after.length;
  const room = Math.max(1, Math.max(0, free) - typedElsewhere);
  const made = parts.slice(0, room).map((email, position) => (
    position === 0
      ? { ...current, email, touched: true }
      : { ...makeRow(email, current.role), touched: true }
  ));
  return {
    rows: [...before, ...made, ...after],
    overflow: Math.max(0, parts.length - room),
  };
};

/** The lines that will actually be sent, as the batch route reads them. */
/**
 * The auxiliary block's stand-in plan id.
 *
 * Those places belong to no plan, which is the whole point of them — but
 * the screen draws one block per id and keys its rows by it. A sentinel
 * keeps the auxiliary users inside the shape the screen already knows
 * instead of growing a second path through every function here.
 *
 * It never leaves this screen: `sendableEntries` turns it into
 * `seat_kind: "support"` with no plan at all, which is what the backend
 * reads.
 */
export const SUPPORT_BLOCK_ID = '__support__';

/** The roles a paid seat may carry, in the order the select offers them. */
export const PLAN_ROLES = ['broker', 'operational', 'admin'] as const;

/**
 * The roles an auxiliary place may carry, operational first because that is
 * what most of them are. The place still has no plan, so a broker here gets
 * no AI agents and no partnerships; the server enforces the same list again.
 */
export const SUPPORT_ROLES = ['operational', 'admin', 'broker'] as const;

/** The roles a block offers. The first is what a new line starts with. */
export const rolesFor = (planId: string): readonly string[] => (
  planId === SUPPORT_BLOCK_ID ? SUPPORT_ROLES : PLAN_ROLES
);

/** The role a new line in this block starts with. */
export const defaultRoleFor = (planId: string): string => rolesFor(planId)[0];

/** A typed role an auxiliary place may carry, or operational when it may not. */
const supportRoleOf = (role: string): string => (
  (SUPPORT_ROLES as readonly string[]).includes(role) ? role : SUPPORT_ROLES[0]
);

/** One line the screen is ready to send, in the shape the API takes. */
export interface SendableEntry {
  email: string;
  role: string;
  plan_id?: string;
  seat_kind?: string;
}

export const sendableEntries = (
  blocks: TeamSeatBlock[],
  rows: RowsByPlan,
): SendableEntry[] => (
  blocks.flatMap((block) => (rows[block.plan_id] || [])
    .filter((row) => row.email.trim())
    .map((row) => (block.plan_id === SUPPORT_BLOCK_ID
      ? {
        email: normalise(row.email),
        // Anything outside the list reads as operational, and the server
        // enforces it again.
        role: supportRoleOf(row.role),
        seat_kind: 'support',
      }
      : {
        email: normalise(row.email),
        role: row.role,
        plan_id: block.plan_id,
      })))
);

/** The little of a catalogue entry this screen needs to name a plan. */
export interface NameablePlan {
  catalogueId?: string;
  value?: string;
  originalId?: string;
  key?: string;
  title?: string;
}

/**
 * The catalogue entry a subscription line points at, or null when none does.
 *
 * A line carries whatever the server stored, and the server resolves a plan
 * by id **or** slug and keeps the id — so the same catalogue is reached by a
 * UUID here and by a slug on the screen that bought it. All four spellings
 * are tried rather than picking one and being wrong half the time.
 *
 * @param catalogue - The plans the app knows about.
 * @param planId - The spelling a block or a seat carries.
 * @returns The entry, or null.
 */
export const planEntryFor = (
  catalogue: NameablePlan[], planId: string,
): NameablePlan | null => catalogue.find((plan) => (
  plan.catalogueId === planId
  || plan.value === planId
  || plan.originalId === planId
  || plan.key === planId
)) ?? null;

/**
 * What to call the plan a block belongs to, or null when nothing knows.
 *
 * The block falls back to an unnamed heading instead of printing a UUID at
 * somebody.
 *
 * @param catalogue - The plans the app knows about.
 * @param planId - The spelling the block carries.
 * @returns Its title, or null.
 */
export const planTitleFor = (
  catalogue: NameablePlan[], planId: string,
): string | null => planEntryFor(catalogue, planId)?.title || null;
